import React, { useState } from 'react';
import {
  Button,
  Stack,
  TextField,
  Typography,
  Avatar,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { Part } from '@domain/product/models/ProductComponent';

interface EditProductPartBottomSheetProps {
  part: Part;
  onSave: (quantity: number) => Promise<void>;
  onClose: () => void;
}

const EditProductPartBottomSheet: React.FC<EditProductPartBottomSheetProps> = ({
  part,
  onSave,
  onClose,
}) => {
  const [quantity, setQuantity] = useState(part.quantity);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (quantity < 1) return;
    setIsSubmitting(true);
    try {
      await onSave(quantity);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <Typography variant="h6">Edit Part</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
      </DialogHeader>

      <Stack sx={{ p: 3 }} spacing={3}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar
            variant="rounded"
            src={part.part.image}
            sx={{
              width: 40,
              height: 40,
              border: 1,
              borderColor: 'divider',
            }}
          />
          <Typography variant="body2">{part.part.title || part.part.ean}</Typography>
        </Stack>

        <TextField
          label="Quantity"
          type="number"
          size="small"
          value={quantity}
          onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
          inputProps={{ min: 1 }}
          fullWidth
        />

        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={quantity < 1 || isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </Stack>
      </Stack>
    </>
  );
};

export default EditProductPartBottomSheet;
